import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useAccounts, useLiabilities, useProfile, useTransactions } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

// Calculate dynamic insights based on real data
const generateInsights = (profile, accounts, liabilities, transactions) => {
  const monthlyIncome = profile.income / 12;
  const totalAssets = accounts.reduce((sum, acc) => sum + Math.max(0, acc.balance), 0);
  const totalDebt = liabilities.reduce((sum, liab) => sum + liab.balance, 0);
  const monthlyDebtPayments = liabilities.reduce((sum, liab) => sum + (liab.monthly_payment || 0), 0);
  
  // Calculate recent spending from transactions (last month)
  const recentTransactions = transactions.filter(tx => 
    tx.date.startsWith('2025-06') && tx.amount < 0
  );
  const monthlySpending = recentTransactions.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  
  const savingsRate = ((monthlyIncome - monthlySpending) / monthlyIncome) * 100;
  const debtToIncomeRatio = (monthlyDebtPayments / monthlyIncome) * 100;
  
  const insights = [];
  
  if (savingsRate >= 20) {
    insights.push(`Excellent! You're saving ${savingsRate.toFixed(1)}% of your income. Keep it above 20%.`);
  } else if (savingsRate >= 10) {
    insights.push(`You're saving ${savingsRate.toFixed(1)}% of your income. Try to increase it to 20%.`);
  } else {
    insights.push(`Your savings rate is ${savingsRate.toFixed(1)}%. Consider reducing expenses to save more.`);
  }
  
  if (debtToIncomeRatio <= 36) {
    insights.push(`Your debt payments are manageable at ${debtToIncomeRatio.toFixed(1)}% of income.`);
  } else {
    insights.push(`Your debt payments are ${debtToIncomeRatio.toFixed(1)}% of income. Consider debt consolidation.`);
  }
  
  const projectedAnnualSavings = (monthlyIncome - monthlySpending) * 12;
  if (projectedAnnualSavings > 0) {
    insights.push(`You're on track to save $${projectedAnnualSavings.toLocaleString()} this year if you maintain current habits.`);
  }
  
  return insights;
};

// Generate actionable recommendations
const generateRecommendations = (profile, accounts, liabilities, transactions) => {
  const monthlyIncome = profile.income / 12;
  const totalAssets = accounts.reduce((sum, acc) => sum + Math.max(0, acc.balance), 0);
  const totalDebt = Math.abs(liabilities.reduce((sum, liab) => sum + liab.balance, 0));
  
  const recommendations = [];
  
  // Emergency fund check
  const emergencyFund = accounts.find(acc => acc.type === 'savings')?.balance || 0;
  const monthlyExpenses = 4000; // Rough estimate from spending
  if (emergencyFund < monthlyExpenses * 3) {
    recommendations.push({
      title: "Build Emergency Fund",
      description: `Aim for $${(monthlyExpenses * 6).toLocaleString()} (6 months of expenses)`,
      priority: "High",
      action: "Save an extra $500/month to your high-yield savings",
      icon: "🏦",
      color: "high"
    });
  }
  
  // Debt payoff
  if (totalDebt > 0) {
    recommendations.push({
      title: "Accelerate Debt Payoff",
      description: `Total debt: $${totalDebt.toLocaleString()}`,
      priority: "Medium",
      action: "Consider paying an extra $200/month toward highest interest debt",
      icon: "💳",
      color: "medium"
    });
  }
  
  // Investment optimization
  const investmentAccounts = accounts.filter(acc => acc.type === 'brokerage' || acc.type === 'retirement');
  const totalInvestments = investmentAccounts.reduce((sum, acc) => sum + acc.balance, 0);
  if (totalInvestments < monthlyIncome * 12) {
    recommendations.push({
      title: "Increase Investment Contributions",
      description: "You have room to invest more for long-term growth",
      priority: "Medium",
      action: "Increase monthly investments by $200-300",
      icon: "📈",
      color: "medium"
    });
  }
  
  return recommendations;
};

const SimpleMoneyFlow = ({ profile, accounts, liabilities }) => {
  const monthlyIncome = profile.income / 12;
  const cashAccounts = accounts.filter(acc => acc.type === 'checking' || acc.type === 'savings');
  const investmentAccounts = accounts.filter(acc => acc.type === 'brokerage' || acc.type === 'retirement');
  const totalDebt = Math.abs(liabilities.reduce((sum, liab) => sum + liab.balance, 0));
  
  return (
    <View style={styles.moneyFlowCard}>
      <Text style={styles.sectionTitle}>Your Money Flow</Text>
      
      <View style={styles.flowContent}>
        {/* Income */}
        <View style={styles.incomeSection}>
          <View style={styles.incomeIconContainer}>
            <Text style={styles.flowIcon}>💰</Text>
          </View>
          <Text style={styles.flowSectionTitle}>Monthly Income</Text>
          <Text style={styles.incomeAmount}>${monthlyIncome.toLocaleString()}</Text>
        </View>
        
        {/* Arrow Down */}
        <View style={styles.arrowContainer}>
          <Text style={styles.arrow}>↓</Text>
        </View>
        
        {/* Distribution */}
        <View style={styles.distributionGrid}>
          {/* Cash & Emergency */}
          <View style={[styles.distributionCard, styles.cashCard]}>
            <Text style={styles.distributionIcon}>🏦</Text>
            <Text style={styles.distributionTitle}>Cash & Emergency</Text>
            <Text style={styles.distributionAmount}>
              ${cashAccounts.reduce((sum, acc) => sum + acc.balance, 0).toLocaleString()}
            </Text>
            <Text style={styles.distributionDesc}>Safety net & daily expenses</Text>
          </View>
          
          {/* Investments */}
          <View style={[styles.distributionCard, styles.investmentCard]}>
            <Text style={styles.distributionIcon}>📈</Text>
            <Text style={styles.distributionTitle}>Investments</Text>
            <Text style={styles.distributionAmount}>
              ${investmentAccounts.reduce((sum, acc) => sum + acc.balance, 0).toLocaleString()}
            </Text>
            <Text style={styles.distributionDesc}>Future growth</Text>
          </View>
          
          {/* Debt */}
          <View style={[styles.distributionCard, styles.debtCard]}>
            <Text style={styles.distributionIcon}>💳</Text>
            <Text style={styles.distributionTitle}>Debt</Text>
            <Text style={styles.distributionAmount}>${totalDebt.toLocaleString()}</Text>
            <Text style={styles.distributionDesc}>Pay down strategically</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const WealthPlan = () => {
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile();
  const { data: accounts, loading: accountsLoading, error: accountsError, refresh: refreshAccounts } = useAccounts();
  const { data: liabilities, loading: liabilitiesLoading, error: liabilitiesError, refresh: refreshLiabilities } = useLiabilities();
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();

  const loading = profileLoading || accountsLoading || liabilitiesLoading || transactionsLoading;
  const error = profileError || accountsError || liabilitiesError || transactionsError;
  
  const refresh = () => {
    refreshProfile();
    refreshAccounts();
    refreshLiabilities();
    refreshTransactions();
  };

  const insights = useMemo(() => {
    if (!profile || !accounts || !liabilities || !transactions) return [];
    return generateInsights(profile, accounts, liabilities, transactions);
  }, [profile, accounts, liabilities, transactions]);

  const recommendations = useMemo(() => {
    if (!profile || !accounts || !liabilities || !transactions) return [];
    return generateRecommendations(profile, accounts, liabilities, transactions);
  }, [profile, accounts, liabilities, transactions]);

  if (loading) return <LoadingState message="Loading wealth plan..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!profile || !accounts || !liabilities || !transactions) return null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Text style={styles.title}>Your Wealth Plan</Text>
        <Text style={styles.subtitle}>Simple strategies to grow your money and reach your goals</Text>
      </View>
      
      {/* Money Flow Visualization */}
      <SimpleMoneyFlow profile={profile} accounts={accounts} liabilities={liabilities} />
      
      {/* Action Plan */}
      <View style={styles.actionPlanCard}>
        <Text style={styles.sectionTitle}>Recommended Actions</Text>
        <View style={styles.recommendationsList}>
          {recommendations.map((rec, index) => (
            <View key={index} style={[styles.recommendationItem, styles[`${rec.color}Priority`]]}>
              <View style={styles.recommendationHeader}>
                <Text style={styles.recommendationIcon}>{rec.icon}</Text>
                <View style={styles.recommendationContent}>
                  <View style={styles.recommendationTitleRow}>
                    <Text style={styles.recommendationTitle}>{rec.title}</Text>
                    <View style={[styles.priorityBadge, styles[`${rec.priority.toLowerCase()}PriorityBadge`]]}>
                      <Text style={[styles.priorityText, styles[`${rec.priority.toLowerCase()}PriorityText`]]}>
                        {rec.priority} Priority
                      </Text>
                    </View>
                  </View>
                  <Text style={styles.recommendationDescription}>{rec.description}</Text>
                  <Text style={styles.recommendationAction}>💡 {rec.action}</Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>
      
      {/* Insights */}
      <View style={styles.insightsCard}>
        <Text style={styles.sectionTitle}>Financial Health Insights</Text>
        <View style={styles.insightsList}>
          {insights.map((insight, idx) => (
            <View key={idx} style={styles.insightItem}>
              <Text style={styles.insightIcon}>💡</Text>
              <Text style={styles.insightText}>{insight}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1024, // max-w-4xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  header: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6B7280',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 24,
  },
  // Money Flow Styles
  moneyFlowCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  flowContent: {
    gap: 32,
  },
  incomeSection: {
    alignItems: 'center',
  },
  incomeIconContainer: {
    width: 96,
    height: 96,
    backgroundColor: '#DCFCE7',
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  flowIcon: {
    fontSize: 48,
  },
  flowSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  incomeAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#059669',
  },
  arrowContainer: {
    alignItems: 'center',
  },
  arrow: {
    fontSize: 32,
    color: '#9CA3AF',
  },
  distributionGrid: {
    flexDirection: 'row',
    gap: 24,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  distributionCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 2,
  },
  cashCard: {
    backgroundColor: '#EFF6FF',
    borderColor: '#BFDBFE',
  },
  investmentCard: {
    backgroundColor: '#F0FDF4',
    borderColor: '#BBF7D0',
  },
  debtCard: {
    backgroundColor: '#FEF2F2',
    borderColor: '#FECACA',
  },
  distributionIcon: {
    fontSize: 24,
    marginBottom: 8,
  },
  distributionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
    textAlign: 'center',
  },
  distributionAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  distributionDesc: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  // Action Plan Styles
  actionPlanCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  recommendationsList: {
    gap: 16,
  },
  recommendationItem: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 2,
  },
  highPriority: {
    backgroundColor: '#FEF2F2',
    borderColor: '#FECACA',
  },
  mediumPriority: {
    backgroundColor: '#FFFBEB',
    borderColor: '#FDE68A',
  },
  recommendationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  recommendationIcon: {
    fontSize: 24,
  },
  recommendationContent: {
    flex: 1,
  },
  recommendationTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
  },
  highPriorityBadge: {
    backgroundColor: '#FEE2E2',
  },
  mediumPriorityBadge: {
    backgroundColor: '#FEF3C7',
  },
  priorityText: {
    fontSize: 12,
    fontWeight: '500',
  },
  highPriorityText: {
    color: '#991B1B',
  },
  mediumPriorityText: {
    color: '#92400E',
  },
  recommendationDescription: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
  },
  recommendationAction: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  // Insights Styles
  insightsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  insightsList: {
    gap: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
  },
  insightIcon: {
    fontSize: 18,
    color: '#2563EB',
  },
  insightText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
});

export default WealthPlan; 